import React, { useState, useEffect, useMemo } from 'react';
import { FolderIcon, FolderPlusIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

// Accepts either:
//   bookmarkId   (string) — single-bookmark mode (original behaviour)
//   bookmarkIds  (array)  — bulk mode; three-state already-in indicator
const AddToCollectionModal = ({ bookmarkId, bookmarkIds: bookmarkIdsProp, isOpen, onClose }) => {
  const ids = useMemo(
    () => bookmarkIdsProp || (bookmarkId ? [bookmarkId] : []),
    [bookmarkIdsProp, bookmarkId]
  );
  const isBulk = ids.length > 1;

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);      // collectionId currently being added
  const [justAdded, setJustAdded] = useState(null); // collectionId added this session
  const [allIn, setAllIn] = useState(new Set());    // collections where ALL ids are already members
  const [someIn, setSomeIn] = useState(new Set());  // collections where SOME (not all) ids are members
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setJustAdded(null);
    setAllIn(new Set());
    setSomeIn(new Set());
    setError(null);
    setShowCreate(false);
    setNewName('');
    setCreating(false);
    setLoading(true);
    bookmarkService.listCollections()
      .then(({ items: data }) => {
        setCollections(data);
        setAllIn(new Set(
          data
            .filter(c => ids.every(id => (c.bookmarkIds || []).includes(id)))
            .map(c => c.collectionId)
        ));
        setSomeIn(new Set(
          data
            .filter(c => {
              const bIds = c.bookmarkIds || [];
              return ids.some(id => bIds.includes(id)) && !ids.every(id => bIds.includes(id));
            })
            .map(c => c.collectionId)
        ));
      })
      .catch(err => setError(err.message || 'Failed to load collections'))
      .finally(() => setLoading(false));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // ids is derived from props; re-running on every render of ids would cause
  // infinite loops in bulk mode. The effect intentionally re-runs only on open/close.

  const handleAdd = async (collectionId) => {
    setAdding(collectionId);
    setError(null);
    try {
      await bookmarkService.addBookmarksToCollection(collectionId, ids);
      setJustAdded(collectionId);
      // Move from someIn → justAdded (no longer partial)
      setSomeIn(prev => { const next = new Set(prev); next.delete(collectionId); return next; });
    } catch (err) {
      setError(err.message || 'Failed to add to collection');
    } finally {
      setAdding(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const created = await bookmarkService.createCollection(trimmed);
      setCollections(prev => [created, ...prev]);
      setShowCreate(false);
      setNewName('');
      await handleAdd(created.collectionId);
    } catch (err) {
      setError(err.message || 'Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const title = isBulk ? `Add ${ids.length} bookmarks to Collection` : 'Add to Collection';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h2>

        {loading && (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {!loading && (
          <>
            {collections.length === 0 && !showCreate && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No collections yet. Create one below.</p>
            )}

            {collections.length > 0 && (
              <ul className="space-y-1 mb-3 max-h-56 overflow-y-auto">
                {collections.map(c => {
                  const isAllIn = allIn.has(c.collectionId);
                  const isSomeIn = someIn.has(c.collectionId);
                  const isJustAdded = justAdded === c.collectionId;
                  const isAdding = adding === c.collectionId;
                  const done = isAllIn || isJustAdded;

                  return (
                    <li key={c.collectionId}>
                      <button
                        onClick={() => !done && handleAdd(c.collectionId)}
                        disabled={isAdding || done}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          done
                            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 cursor-default'
                            : isSomeIn
                            ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {done
                          ? <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                          : isSomeIn
                          ? <FolderPlusIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          : <FolderIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        }
                        <span className="flex-1 text-left truncate">{c.name}</span>
                        {isAdding && <span className="text-xs text-gray-400 dark:text-gray-500">Adding…</span>}
                        {isJustAdded && <span className="text-xs text-green-600 dark:text-green-400">Added</span>}
                        {isAllIn && !isJustAdded && <span className="text-xs text-green-600 dark:text-green-400">All added</span>}
                        {isSomeIn && !isAdding && <span className="text-xs text-amber-600 dark:text-amber-400">Add remaining</span>}
                        {!done && !isSomeIn && !isAdding && <span className="text-xs text-gray-400 dark:text-gray-500">{c.bookmarkCount || 0}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {showCreate ? (
              <form onSubmit={handleCreate} className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  maxLength={100}
                  placeholder="Collection name"
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="submit"
                  disabled={creating || !newName.trim()}
                  className="btn-primary text-xs px-3"
                >
                  {creating ? '…' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                New collection
              </button>
            )}
          </>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose} className="btn-secondary text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCollectionModal;
